import { memo, useMemo } from 'react'

interface Props {
  data: Record<string, any>[]
  maxRows?: number
  title?: string
  priorityColumns?: string[] // Columns to show on mobile
}

// Calculate optimal column widths based on content
const calculateColumnWidths = (data: Record<string, any>[], allKeys: string[]) => {
  const minWidth = 80 // Minimum column width
  const maxWidth = 300 // Maximum column width
  const padding = 24 // px-3 = 12px each side

  const widths: Record<string, number> = {}

  for (const key of allKeys) {
    // Start with header width
    let maxContentWidth = key.length * 8 // Approximate character width

    // Sample first 10 rows to determine content width
    const sampleSize = Math.min(10, data.length)
    for (let i = 0; i < sampleSize; i++) {
      const value = data[i][key]
      const displayValue =
        value === null || value === undefined
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value)

      const contentWidth = displayValue.length * 7 // Approximate character width for content
      maxContentWidth = Math.max(maxContentWidth, contentWidth)
    }

    // Add padding and constrain to min/max
    widths[key] = Math.min(maxWidth, Math.max(minWidth, maxContentWidth + padding))
  }

  return widths
}

// Pre-process row data to avoid expensive operations during render
const processRowData = (row: Record<string, any>, allKeys: string[]) => {
  const processedRow: Record<string, string> = {}
  for (const key of allKeys) {
    const value = row[key]
    processedRow[key] =
      value === null || value === undefined
        ? ''
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value)
  }
  return processedRow
}

// Memoized table row component to prevent unnecessary re-renders
const TableRow = memo(
  ({
    processedRow,
    allKeys,
    priorityColumns,
    columnWidths,
  }: {
    processedRow: Record<string, string>
    allKeys: string[]
    priorityColumns?: string[]
    columnWidths: Record<string, number>
  }) => {
    return (
      <tr>
        {allKeys.map((key, colIndex) => {
          const displayValue = processedRow[key]
          const isPriority = !priorityColumns || priorityColumns.includes(key)
          const hiddenClass = isPriority ? '' : 'hidden sm:table-cell'

          return (
            <td
              key={colIndex}
              className={`border-b border-r border-gray-200 px-3 py-2 text-gray-900 ${hiddenClass}`}
              style={{ width: columnWidths[key] }}
            >
              <div className="truncate">{displayValue}</div>
            </td>
          )
        })}
      </tr>
    )
  }
)

TableRow.displayName = 'TableRow'

export function DataTable({ data, maxRows = 100, title, priorityColumns }: Props) {
  // Memoize expensive calculations and pre-process data
  const { processedData, hasMoreRows, allKeys, desktopOnlyKeys, columnWidths, totalWidth } =
    useMemo(() => {
      if (!data || data.length === 0) {
        return {
          processedData: [],
          hasMoreRows: false,
          allKeys: [],
          desktopOnlyKeys: [],
          columnWidths: {},
          totalWidth: 0,
        }
      }

      const displayData = data.slice(0, maxRows)
      const hasMoreRows = data.length > maxRows
      // Optimize key extraction - only check first few rows for better performance
      const sampleSize = Math.min(10, data.length)
      const allKeys = [...new Set(data.slice(0, sampleSize).flatMap((obj) => Object.keys(obj)))]

      // Calculate optimal column widths
      const columnWidths = calculateColumnWidths(displayData, allKeys)
      const totalWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0)

      // Pre-process all display data once
      const processedData = displayData.map((row) => processRowData(row, allKeys))

      // Separate keys for responsive display
      const desktopOnlyKeys = priorityColumns
        ? allKeys.filter((key) => !priorityColumns.includes(key))
        : allKeys.slice(3)

      return { processedData, hasMoreRows, allKeys, desktopOnlyKeys, columnWidths, totalWidth }
    }, [data, maxRows, priorityColumns])

  if (!data || data.length === 0) {
    return <div className="py-8 text-center text-gray-500">No data to display</div>
  }

  return (
    <div className="space-y-3">
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {desktopOnlyKeys.length > 0 && (
            <div className="text-xs text-gray-500 sm:hidden">Scroll right for more columns</div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div
          className="max-h-[400px] overflow-auto"
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x pan-y',
          }}
        >
          <table
            className="text-left text-sm"
            style={{
              width: Math.max(totalWidth, 400), // Minimum 400px width, but grow with content
              tableLayout: 'fixed',
            }}
          >
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr>
                {allKeys.map((key) => {
                  const isPriority = !priorityColumns || priorityColumns.includes(key)
                  const hiddenClass = isPriority ? '' : 'hidden sm:table-cell'

                  return (
                    <th
                      key={key}
                      className={`whitespace-nowrap border-b border-r border-gray-200 px-3 py-2 font-semibold text-gray-700 ${hiddenClass}`}
                      style={{ width: columnWidths[key] }}
                    >
                      <div className="truncate">{key}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {processedData.map((processedRow, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  processedRow={processedRow}
                  allKeys={allKeys}
                  priorityColumns={priorityColumns}
                  columnWidths={columnWidths}
                />
              ))}
            </tbody>
          </table>
        </div>
        {hasMoreRows && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-700">
            Showing {maxRows} of {data.length} rows
            {desktopOnlyKeys.length > 0 && (
              <span className="ml-2 sm:hidden">• Limited columns on mobile</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
